import { FiStar, FiPlay, FiRotateCcw, FiHome } from 'react-icons/fi';

interface GameOverModalProps {
  isOpen: boolean;
  result: { completed: boolean; score: number; stars: number } | null;
  level: any;
  onNextLevel: () => void;
  onReplay: () => void;
  onMenu: () => void;
}

const GameOverModal = ({ isOpen, result, level, onNextLevel, onReplay, onMenu }: GameOverModalProps) => {
  if (!isOpen || !result) return null;

  const renderStars = (stars: number, maxStars: number = 3) => {
    return (
      <div className="stars-display">
        {[...Array(maxStars)].map((_, i) => (
          <div key={i} className={`star ${i < stars ? 'filled' : ''}`}>
            <FiStar />
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="modal-overlay" onClick={onMenu}>
      <div className="modal-content game-over-modal" onClick={(e) => e.stopPropagation()}>
        {result.completed ? (
          <>
            <h1 className="modal-title success">Уровень пройден!</h1>
            {renderStars(result.stars)}
          </>
        ) : (
          <>
            <h1 className="modal-title failure">Уровень провален</h1>
            <p className="failure-message">Попробуйте ещё раз, чтобы пройти этот уровень!</p>
          </>
        )}

        <div className="score-display">
          <div className="score-label">Score</div>
          <div className="score-value">{result.score.toLocaleString()}</div>
        </div>

        {result.completed && (
          <div className="score-breakdown">
            <div className="score-tier">
              <span>1 Звезда:</span>
              <span className={result.stars >= 1 ? 'achieved' : ''}>{level?.parScore?.toLocaleString()}</span>
            </div>
            <div className="score-tier">
              <span>2 Звезды:</span>
              <span className={result.stars >= 2 ? 'achieved' : ''}>{level?.goldScore?.toLocaleString()}</span>
            </div>
            <div className="score-tier">
              <span>3 Звезды:</span>
              <span className={result.stars >= 3 ? 'achieved' : ''}>{level?.perfectScore?.toLocaleString()}</span>
            </div>
          </div>
        )}

        <div className="modal-actions">
          {result.completed && level?.levelNumber && (
            <button className="btn btn-primary btn-lg" onClick={onNextLevel}>
              <FiPlay /> Следующий уровень
            </button>
          )}
          <button className="btn btn-secondary" onClick={onReplay}>
            <FiRotateCcw /> Переиграть
          </button>
          <button className="btn btn-secondary" onClick={onMenu}>
            <FiHome /> Карта уровней
          </button>
        </div>
      </div>

      <style>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.8);
          backdrop-filter: blur(5px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: var(--spacing-lg);
        }
        .modal-content {
          background: var(--bg-secondary);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-xl);
          padding: var(--spacing-2xl);
          max-width: 500px;
          width: 100%;
          text-align: center;
          animation: scaleIn 0.3s ease;
        }
        .game-over-modal {
          background: linear-gradient(135deg, var(--bg-secondary) 0%, var(--bg-tertiary) 100%);
        }
        .modal-title {
          font-size: 2.5rem;
          font-weight: 800;
          margin-bottom: var(--spacing-lg);
        }
        .modal-title.success {
          background: var(--gradient-success);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .modal-title.failure {
          color: var(--neon-pink);
        }
        .failure-message {
          color: var(--text-secondary);
          margin-top: var(--spacing-sm);
        }
        .stars-display {
          display: flex;
          justify-content: center;
          gap: var(--spacing-md);
          margin-bottom: var(--spacing-xl);
        }
        .star {
          width: 60px;
          height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--bg-tertiary);
          border-radius: var(--radius-md);
          color: var(--text-muted);
          transition: all var(--transition-normal);
        }
        .star.filled {
          background: var(--neon-yellow);
          color: var(--bg-primary);
          animation: pulse 0.5s ease;
        }
        .star svg {
          width: 40px;
          height: 40px;
        }
        .score-display {
          margin-bottom: var(--spacing-xl);
        }
        .score-label {
          font-size: 1rem;
          color: var(--text-secondary);
          margin-bottom: var(--spacing-xs);
        }
        .score-value {
          font-size: 3rem;
          font-weight: 800;
          background: var(--gradient-primary);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .score-breakdown {
          background: var(--bg-card);
          border-radius: var(--radius-md);
          padding: var(--spacing-md);
          margin-bottom: var(--spacing-xl);
        }
        .score-tier {
          display: flex;
          justify-content: space-between;
          padding: var(--spacing-sm) 0;
          border-bottom: 1px solid var(--border-color);
        }
        .score-tier:last-child {
          border-bottom: none;
        }
        .score-tier .achieved {
          color: var(--neon-green);
          font-weight: 600;
        }
        .modal-actions {
          display: flex;
          flex-direction: column;
          gap: var(--spacing-md);
        }
        .btn-lg {
          padding: var(--spacing-md) var(--spacing-xl);
          font-size: 1.125rem;
        }
        @media (max-width: 480px) {
          .modal-content {
            padding: var(--spacing-lg);
          }
          .modal-title {
            font-size: 2rem;
          }
          .star {
            width: 50px;
            height: 50px;
          }
          .star svg {
            width: 30px;
            height: 30px;
          }
          .score-value {
            font-size: 2.5rem;
          }
        }
      `}</style>
    </div>
  );
};

export default GameOverModal;
